import type { Project } from "@/lib/types";
import { slugify } from "@/lib/utils";

// ── Seed (demo) projects ─────────────────────────────────────────
// Read-only base set. User-created projects live in ProjectsProvider
// (localStorage) and are merged ahead of these at runtime.

interface SeedProject {
  id: string;
  name: string;
  desc: string;
  items: Array<{ name: string; tool: string; date: string }>;
}

const SEED: SeedProject[] = [
  {
    id: "b0",
    name: "Client M. — GAD",
    desc: "Generalized anxiety, week 6 of CBT. Working on worry postponement and cognitive restructuring.",
    items: [
      { name: "Session prep — week 6", tool: "Session Prep Brief", date: "Jul 1" },
      { name: "Progress note — week 5", tool: "Progress Note (SOAP)", date: "Jun 26" },
      { name: "Worry cycle map", tool: "Maintenance Cycle Mapper", date: "Jun 27" },
      { name: "Homework: worry window", tool: "Homework Generator", date: "Jun 27" },
    ],
  },
  {
    id: "b1",
    name: "Client R. — Intake",
    desc: "New adult referral for low mood. Intake scheduled; preparing forms and first-session structure.",
    items: [
      { name: "Referral triage snapshot", tool: "Referral Triage Summary", date: "Jun 30" },
      { name: "Intake form (adult, CBT)", tool: "Intake Form Generator", date: "Jun 30" },
      { name: "First-session agenda", tool: "First-Session Agenda", date: "Jun 29" },
    ],
  },
  {
    id: "b2",
    name: "Client T. — Discharge",
    desc: "Completed 14 sessions for panic disorder. Wrapping up with relapse prevention and discharge docs.",
    items: [
      { name: "Relapse prevention plan", tool: "Relapse Prevention Plan", date: "Jun 24" },
      { name: "Discharge summary draft", tool: "Discharge Summary", date: "Jun 24" },
      { name: "Follow-up check-in letter", tool: "Follow-Up Check-In Letter", date: "Jun 23" },
    ],
  },
];

export const SEED_PROJECTS: Project[] = SEED.map((p) => ({
  id: p.id,
  slug: slugify(p.name),
  name: p.name,
  desc: p.desc,
  seed: true,
  items: p.items.map((it) => ({
    name: it.name,
    slug: slugify(it.name),
    tool: it.tool,
    date: it.date,
  })),
}));

export function getSeedProjectBySlug(slug: string): Project | undefined {
  return SEED_PROJECTS.find((p) => p.slug === slug);
}

export function getSeedItemBySlug(
  slug: string,
): { name: string; project: Project } | undefined {
  for (const project of SEED_PROJECTS) {
    const item = project.items.find((it) => it.slug === slug);
    if (item) return { name: item.name, project };
  }
  return undefined;
}

// Month → sortable number, for "recents" / "most recent" ordering.
export const MONTH_NUM: Record<string, number> = {
  Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6,
  Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12,
};

/** Turn a "Jul 1" style date into a sortable integer (month*100 + day). */
export function dateSortKey(date: string): number {
  const [mon, day] = String(date).split(" ");
  return (MONTH_NUM[mon] ?? 0) * 100 + (parseInt(day, 10) || 0);
}
